import { useGetVetterJobQuery } from '@codezo/graphql';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageTitle } from '../../stores/page-title';
import { StatusBadge } from '../../components/ui/status-badge';
import { STAGE_TONE } from '../../lib/job-stage';
import { formatDate } from '../../lib/utils';
import { JobDetail } from '../job/JobDetail';
import { Loading } from '../job/JobPage';
import { JobNotice } from '../job/JobShell';
import type { VetterJob } from '../job/prices/price-form';

/**
 * A job opened from the portal: the link page's sections, loaded by id, in
 * the portal's own page frame — same width, padding and heading as the list.
 * One from another supplier comes back as an error, shown as "not available".
 */
export function JobDetailPage() {
	const { id } = useParams<{ id: string }>();
	const { data, loading, error, refetch } = useGetVetterJobQuery({
		variables: { id },
		fetchPolicy: 'no-cache',
		skip: !id,
	});
	const job = data?.getVetterJob;

	// The quote's name in the top bar's breadcrumb, while this page is open.
	const setTitle = usePageTitle((s) => s.setTitle);
	const quoteName = job?.quoteName ?? null;
	useEffect(() => {
		setTitle(quoteName);
		return () => setTitle(null);
	}, [quoteName, setTitle]);

	return (
		<div className="mx-auto w-full max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			<Link
				to="/jobs"
				className="mb-4 inline-flex items-center gap-1.5 text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				Jobs
			</Link>

			{loading && !job ? (
				<div className="h-40">
					<Loading />
				</div>
			) : !job ? (
				<JobNotice
					title="This job isn't available"
					detail={
						error?.graphQLErrors[0]?.message ??
						error?.message ??
						'It may have been withdrawn, or it belongs to another supplier.'
					}
				/>
			) : (
				<JobDetail job={job} header={<PortalJobHeader job={job} />} onChanged={refetch} />
			)}
		</div>
	);
}

/** Heading in the list page's style, with the stage the list showed. */
function PortalJobHeader({ job }: { job: VetterJob }) {
	const facts = [
		job.quoteDeadline ? `Needed by ${formatDate(job.quoteDeadline)}` : null,
		job.createdAt ? `Received ${formatDate(job.createdAt)}` : null,
	].filter(Boolean);

	return (
		<div className="mb-8">
			<div className="mb-2 flex flex-wrap items-center gap-3">
				<h1 className="text-2xl leading-tight font-medium -tracking-[0.02em] text-text-1 sm:text-3xl">
					{job.quoteName}
				</h1>
				<StatusBadge
					active
					activeLabel={job.stageLabel}
					activeTone={STAGE_TONE[job.stage]}
				/>
			</div>
			{facts.length > 0 && <p className="text-base text-text-3">{facts.join(' · ')}</p>}
		</div>
	);
}
